import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PrefFields } from "./PrefFields";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CustomerPicker } from "@/components/calendar/CustomerPicker";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const empty = { customer_id: "", service_ids: [], employee_id: null, any_employee: true, date_from: null, date_to: null, preferred_days: [], time_from: null, time_to: null, flexible_date: true, flexible_time: false, flexible_employee: false, notes: "" };

export function WaitlistForm({ entry, onClose, onSaved, t, presetCustomerId }) {
    const [f, setF] = useState({ ...empty, ...(entry || {}), customer_id: entry?.customer_id || presetCustomerId || "" });
    const [saving, setSaving] = useState(false);
    const [dup, setDup] = useState(null);
    const set = (patch) => setF((x) => ({ ...x, ...patch }));
    const { data: employees } = useQuery({ queryKey: ["employees"], queryFn: async () => (await api.get("/employees")).data });
    const { data: services } = useQuery({ queryKey: ["services"], queryFn: async () => (await api.get("/services")).data });
    const { data: customers } = useQuery({ queryKey: ["customers"], queryFn: async () => (await api.get("/customers")).data });

    const save = async (force = false) => {
        if (!f.customer_id || !f.service_ids.length) return;
        setSaving(true);
        try {
            const body = { ...f, force };
            if (entry?.id) await api.patch(`/waitlist/${entry.id}`, body); else await api.post("/waitlist", body);
            toast.success(t("common.saved")); onSaved();
        } catch (e) {
            if (e?.response?.data?.detail?.code === "duplicate_waitlist") setDup(e.response.data.detail.entry_id);
            else toast.error(formatApiError(e));
        } finally { setSaving(false); }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto" data-testid="waitlist-form">
                <DialogHeader><DialogTitle>{t("wl.add")}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <CustomerPicker customers={customers || []} value={f.customer_id} onChange={(id) => set({ customer_id: id })} t={t} />
                    <PrefFields f={f} set={set} employees={(employees || []).filter((e) => e.active)} services={(services || []).filter((s) => s.active !== false)} t={t} />
                    <div><Label>{t("calendar.notes")}</Label><Textarea value={f.notes || ""} onChange={(e) => set({ notes: e.target.value })} className="mt-1.5" /></div>
                    {dup && (
                        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="wl-duplicate">
                            <p className="font-semibold text-amber-800">{t("wl.duplicate")}</p>
                            <div className="mt-2 flex gap-2">
                                <Button size="sm" variant="outline" className="rounded-full" onClick={onClose} data-testid="wl-dup-edit">{t("wl.editExisting")}</Button>
                                <Button size="sm" className="rounded-full" onClick={() => save(true)} data-testid="wl-dup-force">{t("wl.createAnyway")}</Button>
                            </div>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} className="rounded-full">{t("common.cancel")}</Button>
                    <Button onClick={() => save(false)} disabled={saving || !f.customer_id || !f.service_ids.length} className="rounded-full" data-testid="save-waiting">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
